import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth.jsx";
import { useI18n } from "../lib/i18n.jsx";
import { toast } from "sonner";
import { Trash2, Sparkles, PenLine, Sun, Star, Cross, Lock } from "lucide-react";
import QuoteOfTheDay from "../components/QuoteOfTheDay.jsx";

const DIARY_BG = "https://static.prod-images.emergentagent.com/jobs/6221bdec-a1f4-4f2a-b3c7-5775bc29aacc/images/8621fe165b5632615dcfc93a42dc1eb35b389ed3ae860f9cc4a6ad565a2a97e4.png";

const RITUAL_ICONS = {
  "lesser-pentagram": Star,
  "sun-greeting": Sun,
  "middle-pillar": Sparkles,
  "rosy-cross": Cross,
};

export default function DiaryPage() {
  const { user } = useAuth();
  const { t, lang, pick } = useI18n();
  const [entries, setEntries] = useState([]);
  const [rituals, setRituals] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const formatDate = useCallback((iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(lang === "nl" ? "nl-NL" : "en-US", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  }, [lang]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [eRes, rRes] = await Promise.all([api.get("/diary"), api.get("/rituals")]);
      setEntries(eRes.data);
      setRituals(rRes.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("diary.toast_load_err"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const logRitual = async (r) => {
    setBusy(true);
    const ritualName = pick(r, "name");
    try {
      const { data } = await api.post("/diary", {
        content: t("diary.performed_template", { ritual: ritualName }),
        ritual_slug: r.slug,
        ritual_name: ritualName,
        entry_type: "ritual",
      });
      setEntries((cur) => [data, ...cur]);
      toast.success(t("diary.toast_inscribed", { ritual: ritualName }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("diary.toast_save_err"));
    } finally {
      setBusy(false);
    }
  };

  const addFree = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/diary", { content: text.trim(), entry_type: "free" });
      setEntries((cur) => [data, ...cur]);
      setText("");
      toast.success(t("diary.toast_entry_inscribed"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("diary.toast_save_err"));
    } finally {
      setBusy(false);
    }
  };

  const removeEntry = async (id) => {
    try {
      await api.delete(`/diary/${id}`);
      setEntries((cur) => cur.filter((e) => e.id !== id));
      toast.success(t("diary.toast_entry_removed"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("diary.toast_save_err"));
    }
  };

  return (
    <main data-testid="diary-page" className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <header className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <p className="label-small text-[#8B3A3A] mb-3">{t("diary.eyebrow")}</p>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-[#1A1918]">
            {user?.name ? <>{t("diary.welcome_named")} <span className="italic">{user.name}</span>.</> : t("diary.welcome_anon")}
          </h1>
          <p className="text-[#4A4238] mt-3 max-w-2xl">{t("diary.intro")}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="grade-pill" data-testid="diary-user-grade">{user?.grade}</span>
        </div>
      </header>

      <QuoteOfTheDay />

      <section className="mb-14">
        <h2 className="font-display text-2xl sm:text-3xl text-[#1A1918] mb-6">{t("diary.today_workings")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-[#C1B49A]">
          {rituals.map((r, i, arr) => {
            const Icon = RITUAL_ICONS[r.slug] || Sparkles;
            const locked = r.locked;
            return (
              <button
                key={r.slug}
                data-testid={locked ? `ritual-locked-btn-${r.slug}` : `ritual-log-btn-${r.slug}`}
                onClick={locked ? undefined : () => logRitual(r)}
                disabled={busy || locked}
                aria-disabled={locked}
                title={locked ? t("rituals.locked_hint") : undefined}
                className={`group text-left p-7 border-[#C1B49A] transition-colors relative overflow-hidden ${
                  locked
                    ? "bg-[#EAE0CA]/20 cursor-not-allowed opacity-60"
                    : "bg-[#EAE0CA]/40 hover:bg-[#EAE0CA]/80"
                }`}
                style={{
                  borderRightWidth: i === arr.length - 1 || (i + 1) % 4 === 0 ? 0 : 1,
                  borderBottomWidth: 1,
                  borderRightStyle: "solid",
                  borderBottomStyle: "solid",
                }}
              >
                <div className="flex items-start justify-between mb-4">
                  <Icon size={26} className={locked ? "text-[#7A7061]" : "text-[#8B3A3A]"} />
                  {locked ? (
                    <Lock size={16} className="text-[#7A7061]" />
                  ) : (
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#7A7061] font-mono group-hover:text-[#8B3A3A]">
                      {t("diary.tap_to_log")}
                    </span>
                  )}
                </div>
                <h3 className="font-display text-xl text-[#1A1918] leading-tight mb-1">{pick(r, "name")}</h3>
                <p className="text-xs text-[#4A4238] italic line-clamp-2">{pick(r, "tagline")}</p>
                {locked && (
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#7A7061] font-mono mt-3">
                    {r.min_grade}+
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <h2 className="font-display text-2xl sm:text-3xl text-[#1A1918] mb-6 flex items-center gap-3">
            <PenLine size={22} className="text-[#8B3A3A]" /> {t("diary.free_heading")}
          </h2>
          <form
            onSubmit={addFree}
            className="border border-[#C1B49A] p-5"
            style={{ backgroundImage: `url(${DIARY_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
            data-testid="diary-free-form"
          >
            <textarea
              data-testid="diary-free-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("diary.textarea_placeholder")}
              rows={8}
              className="textarea-parchment"
            />
            <div className="flex items-center justify-between mt-4">
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#7A7061] font-mono">
                {new Date().toLocaleDateString(lang === "nl" ? "nl-NL" : "en-US", { day: "numeric", month: "long", year: "numeric" })}
              </p>
              <button data-testid="diary-free-submit-btn" type="submit" disabled={busy || !text.trim()} className="btn-primary">
                {t("diary.inscribe_btn")}
              </button>
            </div>
          </form>
        </div>

        <div className="lg:col-span-3">
          <h2 className="font-display text-2xl sm:text-3xl text-[#1A1918] mb-6">{t("diary.liber_heading")}</h2>
          {loading ? (
            <p className="text-[#7A7061] italic">{t("common.loading")}</p>
          ) : entries.length === 0 ? (
            <div className="border border-dashed border-[#C1B49A] p-12 text-center">
              <p className="font-display italic text-2xl text-[#4A4238]">{t("diary.empty_title")}</p>
              <p className="text-sm text-[#7A7061] mt-2">{t("diary.empty_sub")}</p>
            </div>
          ) : (
            <ul className="space-y-0 border border-[#C1B49A] divide-y divide-[#C1B49A]" data-testid="diary-entries-list">
              {entries.map((e) => (
                <li key={e.id} className="p-6 bg-[#EAE0CA]/30 hover:bg-[#EAE0CA]/60 transition-colors" data-testid={`diary-entry-${e.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        {e.entry_type === "ritual" ? (
                          <span className="grade-pill" style={{ borderColor: "#8B3A3A", color: "#8B3A3A" }}>{e.ritual_name || t("diary.label_ritual")}</span>
                        ) : (
                          <span className="grade-pill">{t("diary.label_free")}</span>
                        )}
                        <span className="text-[10px] tracking-[0.22em] uppercase text-[#7A7061] font-mono">{formatDate(e.created_at)}</span>
                      </div>
                      <p className="text-[15px] text-[#1A1918] leading-relaxed whitespace-pre-wrap">{e.content}</p>
                    </div>
                    <button
                      data-testid={`diary-delete-btn-${e.id}`}
                      onClick={() => removeEntry(e.id)}
                      className="text-[#7A7061] hover:text-[#8B3A3A] transition-colors"
                      aria-label="Remove entry"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </main>
  );
}
